/**
 * ─── LAYER: lib ──────────────────────────────────────────────────────────────────
 *
 * ⚠️ [D4.4 §1b] "WHICH DIVISION IS THIS ROW ADDRESSED TO?", ANSWERED IN ONE PLACE.
 *
 * FIX-3b invented the `CD · H` treatment — short codes on screen, full names in a tooltip —
 * for the force grid's source cell, and built it inline there. D4.4 owes the same answer on
 * the fill sheet, the nodal inbox and the printed order preview, and four hand-written
 * copies of "codes here, names in a tooltip" is how four screens end up disagreeing about
 * what a division-only allocation is called.
 *
 * ⚠️ THE CODES ARE THE DISPLAY AND THE NAMES ARE THE ACCESSIBLE TEXT — never the other way
 * round, and never codes alone. A code is unreadable to anyone who has not learned the
 * establishment, so it may be the compact form only where the full name is recoverable
 * without a pointer. `components/AddresseeCodes.tsx` puts the names in the accessible text
 * for exactly that reason; hover is an extra, not the channel.
 *
 * ⚠️ `null` UNIT MEANS "ANY UNIT UNDER THIS DIVISION" — the common case and the fast path,
 * and it renders as a word rather than as a blank. A blank reads as missing data.
 */

/** Just enough of an org unit to name it. `OrgUnitSummaryDto` satisfies this structurally. */
export type AddresseeUnit = { name: string; shortCode: string };

/** What is missing, said out loud. An em-dash reads as "nothing here", which is different. */
const UNKNOWN_DIVISION = { code: '—', name: 'Unknown division' } as const;
const ANY_UNIT = { code: 'Any', name: 'Any unit' } as const;

/**
 * The compact form and the spoken form, together — they are one decision and must not be
 * computed at two call sites, or a screen shows `CD · H` while its tooltip says something
 * else.
 */
export const addresseeLabels = (
  division: AddresseeUnit | null | undefined,
  /**
   * ⚠️ [D4.5 §3d] AN ARRAY IS ONE INBOX ROW: EVERY UNIT ADDRESSED WITHIN THIS DIVISION.
   *
   * §3d groups the inbox by division × duty point × night, so a division asked at its own
   * level AND at one of its stations is ONE row naming both — `CD · A, B, Any`. A `null`
   * MEMBER of the array is the division's own share and renders `Any`, which is the same
   * meaning `fillAllocationSchema.unit` has carried since P4.
   *
   * ⚠️ WIDENED HERE RATHER THAN GIVEN A SECOND COMPONENT. This file's whole subject is that
   * "codes here, names in a tooltip" is decided in ONE place — its header warns about four
   * hand-written copies — and a multi-unit variant would have been the fourth.
   *
   * A single value keeps today's output byte-identical, so the force grid and the fill sheet
   * are untouched.
   */
  unit: AddresseeUnit | null | undefined | readonly (AddresseeUnit | null)[],
): { codes: string; names: string; path: string } => {
  const divisionCode = division?.shortCode ?? UNKNOWN_DIVISION.code;
  const divisionName = division?.name ?? UNKNOWN_DIVISION.name;

  /* ⚠️ A LOCAL PREDICATE, NOT A BARE `Array.isArray`. On a `readonly T[] | T | null` union
     TypeScript narrows `Array.isArray` to `any[]` and every member access below becomes
     unsafe — which the lint catches rather than the compiler. */
  const isMany = (value: typeof unit): value is readonly (AddresseeUnit | null)[] => Array.isArray(value);
  const units = isMany(unit) ? unit : [unit ?? null];
  /* An EMPTY array is not "no units" — it is a row with nothing addressed, which cannot
     happen; treating it as the division's own share keeps the cell readable either way. */
  const members = units.length > 0 ? units : [null];
  const unitCode = members.map((row) => row?.shortCode ?? ANY_UNIT.code).join(', ');
  const unitName = members.map((row) => row?.name ?? ANY_UNIT.name).join(', ');

  /*
   * ─────────────────────────────────────────────────────────────────────────────────
   *  ⚠️ [UX1 §1e] A THIRD STRING — THE HOVER FORM, AND IT IS A PATH.
   *
   *  §1e: hovering a scope chip must give the full path — `CD · E1` → "Central Division >
   *  Jorasanko", `CD · Any` → "Central Division > Any". A short code is unreadable to anybody
   *  who has not learned the establishment, and the chip is now the left-hand column of every
   *  slots line, so it is read constantly.
   *
   *  ⚠️ `names` IS NOT REPOINTED AT IT, AND THAT IS THE WHOLE REASON THERE ARE THREE STRINGS.
   *  `names` is the ACCESSIBLE text — the `sr-only` span — and `>` is announced as "greater
   *  than" by several screen readers. This file's own header says hover is the convenience and
   *  the accessible text is the channel; making the channel worse to improve the convenience
   *  would invert that.
   *
   *  ⚠️ AND `Any unit` BECOMES `Any` HERE ONLY. A path segment names a place; "Central Division
   *  > Any unit" reads as a sentence that has lost a word. The spoken form keeps the noun.
   *
   *  ⚠️ THERE IS NO WIRE CHANGE. `OrgUnitSummaryDto` carries no ancestors, and it needs none:
   *  division + unit IS the full path of an allocation, which is the only thing this renders.
   * ─────────────────────────────────────────────────────────────────────────────────
   */
  const unitPath = members.map((row) => row?.name ?? ANY_UNIT.code).join(', ');

  return {
    codes: `${divisionCode} · ${unitCode}`,
    names: `${divisionName} · ${unitName}`,
    path: `${divisionName} > ${unitPath}`,
  };
};
